import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon, CropFree as CropIcon, Refresh as RetakeIcon, RotateLeft as RotateLeftIcon, RotateRight as RotateRightIcon } from '@mui/icons-material';

/**
 * CaptureDialog: capture from screen → crop area → return processed image.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onCapture: (file: File) => void — called with the cropped+compressed JPEG
 *   documentType: string — label like "Passport Copy" shown in the title
 */
const CaptureDialog = ({ open, onClose, onCapture, documentType }) => {
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedUrl, setCapturedUrl] = useState(null);
  const [cropping, setCropping] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [rotation, setRotation] = useState(0);
  const canvasRef = useRef(null);
  const startRef = useRef(null);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => { if (capturedUrl) URL.revokeObjectURL(capturedUrl); };
  }, [capturedUrl]);

  // Auto-trigger capture when dialog opens
  useEffect(() => {
    if (open && !capturedBlob) {
      handleCapture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'never' },
        audio: false,
      });

      const track = stream.getVideoTracks()[0];

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      // Wait for video to have real dimensions
      await new Promise((r) => setTimeout(r, 300));
      const w = video.videoWidth;
      const h = video.videoHeight;

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(video, 0, 0, w, h);

      track.stop();
      video.srcObject = null;

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (capturedUrl) URL.revokeObjectURL(capturedUrl);
      const url = URL.createObjectURL(blob);
      setCapturedBlob(blob);
      setCapturedUrl(url);
      setCropRect(null);
      setCropping(false);
      setRotation(0);
      setImgSize({ w, h });
    } catch (err) {
      console.log('Capture cancelled:', err.message);
      onClose();
    }
  };

  const handleMouseDown = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    startRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setCropping(true);
    setCropRect(null);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!startRef.current || !cropping) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    setCropRect({
      x: Math.min(startRef.current.x, x),
      y: Math.min(startRef.current.y, y),
      width: Math.abs(x - startRef.current.x),
      height: Math.abs(y - startRef.current.y),
    });
  }, [cropping]);

  const handleMouseUp = useCallback(() => {
    startRef.current = null;
    if (cropRect && cropRect.width > 10 && cropRect.height > 10) {
      setCropping(false);
    } else {
      setCropRect(null);
      setCropping(false);
    }
  }, [cropRect]);

  const handleConfirm = async () => {
    if (!capturedBlob) return;

    let finalBlob = capturedBlob;

    // If cropped, extract the cropped region
    if (cropRect) {
      const img = await createImageBitmap(capturedBlob);
      const displayEl = document.getElementById('capture-preview-img');
      if (displayEl) {
        const displayW = displayEl.clientWidth;
        const displayH = displayEl.clientHeight;
        const scaleX = img.width / displayW;
        const scaleY = img.height / displayH;

        const canvas = document.createElement('canvas');
        const cw = Math.round(cropRect.width * scaleX);
        const ch = Math.round(cropRect.height * scaleY);
        canvas.width = cw;
        canvas.height = ch;
        canvas.getContext('2d').drawImage(
          img,
          Math.round(cropRect.x * scaleX), Math.round(cropRect.y * scaleY), cw, ch,
          0, 0, cw, ch
        );
        img.close();
        finalBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      } else {
        img.close();
      }
    }

    // Compress to JPEG with optional rotation
    const bmp = await createImageBitmap(finalBlob);
    const maxDim = 2048;
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
    const sw = Math.round(bmp.width * scale);
    const sh = Math.round(bmp.height * scale);
    const isRotated = rotation % 180 !== 0;
    const canvas = document.createElement('canvas');
    canvas.width = isRotated ? sh : sw;
    canvas.height = isRotated ? sw : sh;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(bmp, -sw / 2, -sh / 2, sw, sh);
    bmp.close();

    const jpegBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.80));
    const file = new File([jpegBlob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });

    onCapture(file);
    handleClose();
  };

  const handleClose = () => {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedBlob(null);
    setCapturedUrl(null);
    setCropRect(null);
    setCropping(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg"
      PaperProps={{ sx: { maxHeight: '90vh', maxWidth: '90vw' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CropIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Capture {documentType || 'Document'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {capturedUrl ? (
          <>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
              Drag to select the area you want to capture
            </Typography>
            <Box
              sx={{ position: 'relative', cursor: 'crosshair', userSelect: 'none', display: 'inline-block' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              <img
                id="capture-preview-img"
                src={capturedUrl}
                alt="Captured screen"
                style={{ maxHeight: '70vh', maxWidth: '80vw', display: 'block', transform: `rotate(${rotation}deg)`, transition: 'transform 0.2s' }}
                draggable={false}
              />
              {/* Crop selection overlay */}
              {cropRect && cropRect.width > 0 && (
                <>
                  {/* Dark overlay outside crop area */}
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.5)', pointerEvents: 'none',
                    clipPath: `polygon(0% 0%, 0% 100%, ${cropRect.x}px 100%, ${cropRect.x}px ${cropRect.y}px, ${cropRect.x + cropRect.width}px ${cropRect.y}px, ${cropRect.x + cropRect.width}px ${cropRect.y + cropRect.height}px, ${cropRect.x}px ${cropRect.y + cropRect.height}px, ${cropRect.x}px 100%, 100% 100%, 100% 0%)`,
                  }} />
                  {/* Crop border */}
                  <Box sx={{
                    position: 'absolute',
                    left: cropRect.x, top: cropRect.y,
                    width: cropRect.width, height: cropRect.height,
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                  }} />
                </>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">Waiting for screen capture...</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        {capturedUrl && (
          <Box sx={{ display: 'flex', gap: 0.5, mr: 'auto' }}>
            <Button size="small" startIcon={<RetakeIcon />} onClick={handleCapture}
              sx={{ textTransform: 'none' }}>
              Retake
            </Button>
            <IconButton size="small" title="Rotate left" onClick={() => setRotation((r) => (r - 90 + 360) % 360)}>
              <RotateLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" title="Rotate right" onClick={() => setRotation((r) => (r + 90) % 360)}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Button size="small" onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleConfirm}
          disabled={!capturedUrl}
          sx={{ textTransform: 'none' }}>
          {cropRect ? 'Use Selected Area' : 'Use Full Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaptureDialog;
